import * as React from 'react';
import ConfirmPopup from './ConfirmPopup';
import AllocateAddItemModal from './AllocateAddItemModal';
import DriverJobsPhotosModal from './DriverJobsPhotos';
import axios from 'axios';
import authService from './api-authorization/AuthorizeService';
import { toast } from 'react-toastify';

interface DriverJobsListProps {
    truckIndex: number;
    truckId: number;
    truckJobList: TruckJobList[];
    truckOptions: GenericList[];
    scheduleTypes: GenericList[];
    updateTruckIndex(truckIndex: number): void;
    departOffice(): void;
    arriveOffice(): void;
    arriveSite(jobIndex: number): void;
    departSite(jobIndex: number): void;
    showSiteIssues(jobIndex: number): void;
    goToolCheck(): void;
    saveLunch(scheduleId: number, jobId: number): void;
    updateJobs(jobs: DriverJob[]): void;
    refreshJobs(): void;
}

interface TruckJobList {
    truckId: number;
    estimatedSiteTimeFormatted: string;
    actualSiteTimeFormatted: string;
    estimatedTravelTimeFormatted: string;
    actualTravelTimeFormatted: string;
    truckCheckComplete: boolean;
    truckToolCheckComplete: boolean;
    truckFinalToolCheckComplete: boolean;
    allJobsComplete: boolean;
    hasDepartedOffice: boolean;
    hasArrivedOffice: boolean;
    hadLunch: boolean;
    truckNotes: string;
    jobs: DriverJob[];
}

interface DriverJobsList {
    loading: boolean;
    showLunch: boolean;
    showNewItemModal: boolean;
    newItem: AllocateNewItem;
    showConfirmCancel: boolean;
    cancelIndex: number;
    showPhotoImport: boolean;
    jobId: number;
    errors: any;
}

interface DriverJob {
    id: number;
    jobId: number;
    truckId: number;
    officeAddressLatitude: number;
    officeAddressLongitude: number;
    siteAddress: string;
    siteAddressGoogle: string;
    siteAddressLatitude: number;
    siteAddressLongitude: number;
    siteContact: string;
    customer: string;
    gateCode: string;
    estimatedTime: string;
    jobNotes: string[];
    items: DriverJobItems[];
    hasArrived: boolean;
    isComplete: boolean;
    truckAccess: boolean;
    parking: boolean;
    siteClean: boolean;
    powerlinesWithinRange: boolean;
    siteHasIssues: boolean;
    showSiteIssues: boolean;
    isAssistJob: boolean;
    isManualJob: boolean;
    isDriverJob: boolean;   //means the driver added so can move
    vehicleItemOrder: number;
    plans: PlanFile[];
}

interface DriverJobItems {
    id: number;
    kwikstageId: number;
    unitNumberDisplay: string;
    type: string;
    typeId: number;
    item: string;
    itemId: number;
    quantity: number;
    itemQuantityType: string;
    estimatedTime: string;
    estimatedTimeMins: number;
    isComplete: boolean;
    percentComplete: number;    //used for Kwikstage
    vehicleItemOrder: number;   //used when adding manual job after a linked job
    itemParts: ItemPart[];
}

interface ItemPart {
    itemPartId: number;
    part: string;
    isComplete: boolean;
}

interface PlanFile {
    id: number;
    fileName: string;
}

interface GenericList {
    id: number;
    name: string;
}

interface AllocateNewItem {
    jobId: number;
    siteAddress: string;
    googleAddress: string;
    googleLatitude: number;
    googleLongitude: number;
    customerName: string;
    contactName: string;
    scheduleDate: string;
    scheduleBranchId: number;
    description: string;
    typeId: number;
    itemTypeGroupId: number;
    notes: string;
    runsheetNotes: string;
    subId: string;
}

export default class DriverJobsListData extends React.Component<DriverJobsListProps, DriverJobsList> {
    constructor(props: DriverJobsListProps) {
        super(props);

        this.state = {
            loading: false,
            showLunch: false,
            showNewItemModal: false,
            newItem: {
                jobId: 0,
                siteAddress: "",
                googleAddress: "",
                googleLatitude: 0,
                googleLongitude: 0,
                customerName: "",
                contactName: "",
                scheduleDate: "",
                scheduleBranchId: 1,
                description: "",
                typeId: -1,
                itemTypeGroupId: -1,
                notes: "",
                runsheetNotes: "",
                subId: ""
            },
            showConfirmCancel: false,
            cancelIndex: -1,
            showPhotoImport: false,
            jobId: 0,
            errors: {}
        };
    }

    componentDidMount() {
        window.scrollTo(0, 0);
    }

    updateTruck = (e: any) => {
        let truckId = e.target.value * 1;

        var truckOptions = this.props.truckOptions;
        var truckIndex = truckOptions.findIndex(t => t.id === truckId);

        this.props.updateTruckIndex(truckIndex);
    }

    departOffice = () => {
        this.props.departOffice();
    }

    arriveOffice = () => {
        this.props.arriveOffice();
    }

    arriveSite = (e: any, jobIndex: number) => {
        e.preventDefault();

        this.props.arriveSite(jobIndex);
    }

    departSite = (e: any, jobIndex: number) => {
        e.preventDefault();

        //check if install job and therefore must have at least 4 photos uploaded
        this.readyForDeparture(jobIndex);     
        //this.props.departSite(jobIndex);
    }

    readyForDeparture = async (jobIndex: number) => {
        //if an install job then check to make sure at least 4 photos have been uploaded
        var truckJobList = this.props.truckJobList;
        var jobs = truckJobList[this.props.truckIndex].jobs;
        var job = jobs[jobIndex];

        var hasInstallItems = false;
        for (var i = 0; i < job.items.length; i++) {
            let item = job.items[i];
            if (item.typeId === 1) {
                hasInstallItems = true;
                break;
            }
        }

        if (hasInstallItems && !job.isAssistJob && !job.isManualJob) {
            //check if 4 photos uploaded
            const token = await authService.getAccessToken();

            axios.get('api/Job/GetJobPhotoCount?jobId=' + job.jobId, {
                headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
            })
            .then(res => {
                var photoCount = res.data;
                if (photoCount < 4) {
                    //toast.error("Please upload at least 4 photos of the job before departing!");
                    toast.info("Remember to upload at least 4 photos to this job!");
                    this.props.departSite(jobIndex);
                } else {
                    this.props.departSite(jobIndex);
                }
            }).catch(error => {
                this.props.departSite(jobIndex);
            });
        } else {
            this.props.departSite(jobIndex);
        }
    }

    showSiteIssues = (e: any, jobIndex: number) => {
        e.preventDefault();

        this.props.showSiteIssues(jobIndex);
    }

    goToolCheck = (e: any) => {
        e.preventDefault();

        this.props.goToolCheck();
    }

    //lunch
    addLunch = () => {
        this.setState({ showLunch: true });
    }

    hideLunch = () => {
        this.setState({ showLunch: false });
    }

    saveLunch = () => {
        //find job to add lunch break to
        var truckJobList = this.props.truckJobList;
        var jobs = truckJobList[this.props.truckIndex].jobs;
        let scheduleId = 0;
        let jobId = 0;
        var currentJobs = jobs.filter(j => j.hasArrived && !j.isComplete);
        if (currentJobs.length > 0) {
            //should only be one but get last one
            let currentJob = currentJobs[currentJobs.length - 1];
            scheduleId = currentJob.id;
            jobId = currentJob.jobId;
        };

        this.setState({ showLunch: false });
        this.props.saveLunch(scheduleId, jobId)
    }
    //end lunch

    //Upload Photos
    showUploadPhotos = (e: any, itemIndex: number) => {
        e.preventDefault();

        var truckJobList = this.props.truckJobList;
        var jobs = truckJobList[this.props.truckIndex].jobs;
        var job = jobs[itemIndex];

        this.setState({ showPhotoImport: true, jobId: job.jobId });
    }

    hideUploadPhotos = () => {
        this.setState({ showPhotoImport: false, jobId: 0 });
    }

    //End Upload Photos

    //Add New Item Modal 
    hideNewItemModal = () => {
        //reset data
        var newItem = this.resetNewItem();

        this.setState({ newItem: newItem, showNewItemModal: false });
    }

    addNewItemModal = (e: any) => {
        e.preventDefault();

        var newItem = this.resetNewItem();
        this.setState({ newItem: newItem, showNewItemModal: true });
    }

    updateNewItemData = (updatedNewItem: AllocateNewItem) => {
        this.setState({ newItem: updatedNewItem });
    }

    resetNewItem = () => {
        var newItem: AllocateNewItem = {
            jobId: 0,
            siteAddress: "",
            googleAddress: "",
            googleLatitude: 0,
            googleLongitude: 0,
            customerName: "",
            contactName: "",
            scheduleDate: "",
            scheduleBranchId: 1,
            description: "",
            typeId: -1,
            itemTypeGroupId: -1,
            notes: "",
            runsheetNotes: "",
            subId: ""
        }
        return newItem;
    }

    saveNewItemData = () => {
        this.saveNewAllocation();
    }

    async saveNewAllocation() {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        //work out where in the list the job should go
        var truckJobList = this.props.truckJobList;
        var truckJob = truckJobList[this.props.truckIndex];
        var jobs = truckJob.jobs;
        var nextIndex = 0;
        var jobsStarted = jobs.filter(j => j.isComplete || j.hasArrived);
        if (jobsStarted.length > 0) {
            //get last job order number
            var lastJob = jobsStarted[jobsStarted.length - 1];
            var lastJobItems = lastJob.items;
            var lastJobItemIndex = lastJobItems.length;
            if (lastJobItemIndex > 0) {
                var lastVehicleOrder = lastJobItems[lastJobItemIndex - 1].vehicleItemOrder;
                nextIndex = lastVehicleOrder + 1;
            } else {
                nextIndex = lastJob.vehicleItemOrder + 1;
            }
        }

        var addItem = {
            siteAddress: this.state.newItem.siteAddress,
            googleAddress: this.state.newItem.googleAddress,
            googleLatitude: this.state.newItem.googleLatitude,
            googleLongitude: this.state.newItem.googleLongitude,
            customerName: this.state.newItem.customerName,
            contactName: this.state.newItem.contactName,
            description: this.state.newItem.description,
            typeId: this.state.newItem.typeId,
            runsheetNotes: this.state.newItem.runsheetNotes,
            truckId: truckJob.truckId,
            orderNumber: nextIndex,
            subId: user.sub
        };

        axios.post('api/Job/SaveDriverManualJob', addItem, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            toast.success("New Job Added Successfully");
            var newItem = this.resetNewItem();
            this.setState({ newItem: newItem, showNewItemModal: false });
            this.props.refreshJobs();
        })
        .catch(error => {
            toast.error(error.message);
        });
            
    }
    //End New Item Modal

    //delete manual driver job
    cancelDriverManualJob = (e: any, index: number) => {
        e.preventDefault();

        this.setState({ showConfirmCancel: true, cancelIndex: index });
    }

    hideConfirmCancel = () => {
        this.setState({ showConfirmCancel: false, cancelIndex: -1 });
    }

    saveConfirmCancel = () => {
        this.saveCancelJob();
    }

    saveCancelJob = async () => {
        const token = await authService.getAccessToken();

        var truckJobList = this.props.truckJobList;
        var jobs = truckJobList[this.props.truckIndex].jobs;
        var job = jobs[this.state.cancelIndex];

        let cancelJob = {
            id: job.id
        }

        axios.post('api/Job/DeleteDriverManualJob', cancelJob, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            toast.success("Job Cancelled Successfully");
            this.setState({ showConfirmCancel: false, cancelIndex: -1 });
            this.props.refreshJobs();
        })
        .catch(error => {
            toast.error(error.message);
        });
    }
    //end delete manual driver job

    downloadPlan = (e: any, id: number) => {
        e.preventDefault();
        this.getPlanFile(id);
    }

    async getPlanFile(id: number) {
        const token = await authService.getAccessToken();

        axios('api/Job/GetJobPlans?Id=' + id, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` },
            method: 'GET',
            responseType: 'blob' //Force to receive data in a Blob Format
        })
        .then(res => {
            //DOWNLOAD FILE TO BROWSER
            var fileheader = res.headers["content-disposition"];
            var filename = "plans.pdf";
            if (fileheader) {
                filename = fileheader.split('filename=')[1].split(';')[0];
                filename = filename.replaceAll('"', '');
            }
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    //MOVE DRIVER ADDED JOBS
    handleDragStart = (event: React.DragEvent<HTMLDivElement>, oldIndex: number) => {
        event.dataTransfer.setData('text', oldIndex.toString());
    }

    enableDropping = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
    }

    handleDrop = (event: React.DragEvent<HTMLDivElement>, newIndex: number) => {
        const oldIndexText = event.dataTransfer.getData('text');
        var oldIndex = parseInt(oldIndexText);
        this.onSortEnd(oldIndex, newIndex);
    }

    onSortEnd = (oldIndex: number, newIndex: number) => {
        var truckJobList = this.props.truckJobList;
        var jobs = truckJobList[this.props.truckIndex].jobs;

        jobs.splice(newIndex, 0, jobs.splice(oldIndex, 1)[0]);

        //loop through the jobs and update the order  
        //for (var i = 0; i < jobs.length; i++) {
        //    //jobs[i]. = (i + 1);
        //}

        this.props.updateJobs(jobs);
    };
    //END MOVE DRIVER ADDED JOBS

    render() {
        let contents = this.state.loading
            ? <p><em>Loading...</em></p>
            : this.renderDetails();

        return <div>
            {contents}
        </div>;
    }

    renderDetails = () => {
        let truckJobList = this.props.truckJobList;
        var hasJobs = truckJobList.length > 0;
        var powerlinesText = "Powerlines < 4.6 m";
        let truckJobs = hasJobs ? truckJobList[this.props.truckIndex] : null;
        var lastJobIndex = truckJobs ? truckJobs.jobs.length - 1 : 0;

        let confirmLunch = <ConfirmPopup heading="Lunch Break" text="Stop for a 30 minute lunch break?" hideConfirmModal={this.hideLunch} showConfirmModal={this.state.showLunch} saveConfirmModal={this.saveLunch} />
        let confirmCancel = <ConfirmPopup heading="Cancel Job" text="Are you sure you want to cancel manually added job?" hideConfirmModal={this.hideConfirmCancel} showConfirmModal={this.state.showConfirmCancel} saveConfirmModal={this.saveConfirmCancel} />
        let addItem = <AllocateAddItemModal systemCompanyId={2} newItem={this.state.newItem} showNewItemModal={this.state.showNewItemModal} scheduleTypes={this.props.scheduleTypes} itemTypeGroups={[]} driverJob={true} hideModal={this.hideNewItemModal} updateData={this.updateNewItemData} save={this.saveNewItemData} />
        let uploadPhotos = <DriverJobsPhotosModal jobId={this.state.jobId} showModal={this.state.showPhotoImport} hideModal={this.hideUploadPhotos } />

        let truckNote = "Complete Truck and Tool Check before Departing Office";

        return (
            <div>
                <div className="static-modal">
                    {confirmLunch}
                    {confirmCancel}
                    {addItem}
                    {uploadPhotos}
                </div>

                <label className={this.props.truckOptions.length < 2 ? "hidden" : "input-group makeitflexspacebetween marginBottom10 bg-light"} htmlFor="truckFilterId">
                    <span className="label-mobile--inline-sm margin5">Truck</span>
                    <select className="select margin5" id="truckId" name="truckId" value={this.props.truckId} onChange={(e) => this.updateTruck(e)} disabled={truckJobs != null && truckJobs.hasDepartedOffice && !truckJobs.hasArrivedOffice}>
                        <option hidden defaultValue="-1"></option>
                        {this.props.truckOptions.map(truck =>
                            <option key={truck.id} value={truck.id}>{truck.name}</option>
                        )};
                    </select>
                </label>
                <div className={hasJobs ? "hidden" : ""}>There are no jobs assigned to you for today!</div>
                <div className={truckJobs && this.props.truckIndex >= 0 ? "" : "hidden"}>
                    <div className="makeitflexspacebetween makeitcentervertical">
                        <h2 className="mobile-margin">Jobs</h2>
                        <div className={!hasJobs || truckJobs?.hasDepartedOffice ? "hidden" : "marginBottom10"}>
                            <button className="mobile-button--large" type="button" onClick={this.departOffice} disabled={!truckJobs?.truckCheckComplete || !truckJobs?.truckToolCheckComplete} >Depart Office</button>
                        </div>
                        <div className={truckJobs?.hasDepartedOffice && truckJobs?.allJobsComplete && !truckJobs?.hasArrivedOffice ? "marginBottom10" : "hidden"}>
                            <button className="mobile-button--large" type="button" onClick={this.arriveOffice}>Arrive Office</button>
                        </div>
                        <div className={truckJobs?.hasDepartedOffice && hasJobs && !truckJobs?.hadLunch ? "marginBottom10" : "hidden"}>
                            <button className="mobile-button--large" type="button" onClick={this.addLunch}>Lunch</button>
                        </div>
                    </div>
                    <div>
                        <span className={!hasJobs ? "" : !truckJobs?.truckCheckComplete || !truckJobs?.truckToolCheckComplete ? "makeitred" : ""}>{!truckJobs?.truckCheckComplete || !truckJobs?.truckToolCheckComplete ? truckNote : ""}</span>
                    </div>
                    <div className="makeitflexspacebetween makeitcentervertical">
                        <span>Version 1.1</span>
                        <i className={hasJobs ? "mobile-icon-green marginLeft10 fas fa-circle-plus mobileMenuSize" : ""} onClick={(e) => this.addNewItemModal(e)}></i>
                    </div>
                    <div className={!hasJobs || truckJobs?.truckNotes === "" ? "hidden" : ""}>
                        <hr className="hr--no-margin"></hr>
                        <div>{truckJobs?.truckNotes}</div>
                    </div>

                    <hr className="hr--no-margin"></hr>
                    <div className={!hasJobs ? "hidden" : "" }>
                        {truckJobs?.jobs.map((job, index) =>
                            <div key={index} id={job.isDriverJob ? "drag" : ""} draggable={job.isDriverJob} onDragStart={(e) => this.handleDragStart(e, index)} onDragOver={this.enableDropping} onDrop={(e) => this.handleDrop(e, index)}>
                                <div className="makeitflexspacebetween makeitcentervertical">
                                    <div className="driverjobs-min20">
                                        {index + 1}
                                    </div>
                                    <div className="makeitbold marginRight10 driverjobs-address">
                                        {job.siteAddress}
                                        <a className={job.isDriverJob ? "hidden" : "marginLeft10"} href={job.siteAddressGoogle} target="_blank" >
                                            <span className="fas fa-map-marker-alt edit--icon"></span>
                                        </a>
                                        <a className={job.isDriverJob && job.isManualJob && !job.hasArrived && !job.isComplete ? "marginLeft10" : "hidden"} href="#" onClick={(e) => this.cancelDriverManualJob(e, index)} >
                                            <span className="makeitred fas fa-times-circle edit--icon"></span>
                                        </a>
                                        <span className={!job.isManualJob && job.isComplete ? "marginLeft10" : "hidden"}>
                                            <i className="fa-solid fa-camera edit--icon--large makeitdashgreen" onClick={(e) => this.showUploadPhotos(e, index)}></i>
                                        </span>
                                    </div>
                                </div>
                                <div className={job.isComplete ? "hidden" : "makeitflexspacebetween makeitcentervertical"}>
                                    <div>
                                        {job.customer}
                                    </div>
                                    <div className="makeitbold">
                                        <span className={job.gateCode ? "" : "hidden"}>g/c: </span>{job.gateCode}
                                    </div>
                                </div>
                                <div className={job.isComplete ? "hidden" : "makeitflexspacebetween makeitcentervertical"}>
                                    <div>
                                        {job.siteContact}
                                    </div>
                                    <div className={job.siteHasIssues ? "" : "hidden"}>
                                        <i className="fas fa-ban edit--icon--large makeitred" onClick={(e) => this.showSiteIssues(e, index)}></i>
                                    </div>
                                    <div className={job.isManualJob ? "hidden" : ""}>
                                        <i className="fa-solid fa-camera edit--icon--large makeitdashgreen" onClick={(e) => this.showUploadPhotos(e, index)}></i>
                                    </div>
                                </div>
                                <div className={job.showSiteIssues && !job.isComplete ? "makeitred" : "hidden"}>
                                    <div className={job.truckAccess ? "hidden" : ""}>No Truck Access</div>
                                    <div className={job.parking ? "hidden" : ""}>No Parking Available</div>
                                    <div className={job.siteClean ? "hidden" : ""}>Site is not clean</div>
                                    <div className={job.powerlinesWithinRange ? "" : "hidden"}> {powerlinesText}</div>
                                </div>
                                <div className={job.isComplete ? "hidden" : ""}>
                                    {job.items.map((item, index) =>
                                        <div key={index} className="makeitflexspacebetween">
                                            <div>
                                                {item.unitNumberDisplay === "" ? "" : item.unitNumberDisplay + " - "}
                                                {item.type}
                                            </div>
                                            <div>
                                                {item.item}
                                            </div>
                                            <div>
                                                {item.itemQuantityType}
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <div className={job.isComplete ? "hidden" : "makeititalic driverjobs--notes"}>
                                    <ul>
                                        {job.jobNotes.map((note, index) => 
                                            <div key={index} className={note =="" ? "hidden" : ""}><li>{note}</li></div>
                                        )}
                                    </ul>
                                </div>
                                <div className={job.isComplete ? "hidden" : "alignitemscentre"}>
                                    <div className={job.plans.length > 0 ? "" : "hidden"}>
                                        {job.plans.map((plan, index) =>
                                            <div key={plan.id}>
                                                <a href="#" onClick={(e) => this.downloadPlan(e, plan.id)}>{plan.fileName}</a>
                                            </div>
                                        )}

                                    </div>
                                </div>
                                <div>
                                    <button className={job.hasArrived || job.isComplete || !truckJobs?.hasDepartedOffice || index > 0 && !truckJobs ?.jobs[index - 1].isComplete ? "hidden" : "mobile-button--fill mobile-button--green"} type="button" onClick={(e) => this.arriveSite(e, index)}>Arrived</button>
                                    <button className={((lastJobIndex === index && truckJobs?.truckFinalToolCheckComplete) || lastJobIndex !== index) && job.hasArrived && !job.isComplete ? "mobile-button--fill mobile-button--green" : "hidden"} type="button" onClick={(e) => this.departSite(e, index)}>Departing</button>
                                    <button className={lastJobIndex === index && !truckJobs?.truckFinalToolCheckComplete && job.hasArrived && !job.isComplete ? "mobile-button--fill mobile-button--green" : "hidden"} type="button" onClick={(e) => this.goToolCheck(e)}>Tool Check</button>
                                </div>
                                <hr className="hr--no-margin"></hr>
                            </div>
                        )}
                    </div>
                    <div className="makeitflexspacebetween">
                        <label className="input-group" htmlFor="estimatedSiteTimeFormatted">
                            <span className="label-mobile--inline">Est. Site Time:</span>
                            {truckJobs?.estimatedSiteTimeFormatted}
                        </label>
                        <label className="input-group" htmlFor="actualSiteTimeFormatted">
                            <span className="label-mobile--inline">Act. Site Time: </span>
                            {truckJobs?.actualSiteTimeFormatted}
                        </label>
                    </div>
                    <div className="makeitflexspacebetween">
                        <label className="input-group" htmlFor="estimatedSiteTimeFormatted">
                            <span className="label-mobile--inline">Est. Travel Time:</span>
                            {truckJobs?.estimatedTravelTimeFormatted}
                        </label>
                        <label className="input-group" htmlFor="actualSiteTimeFormatted">
                            <span className="label-mobile--inline">Act. Travel Time: </span>
                            {truckJobs?.actualTravelTimeFormatted}
                        </label>
                    </div>
                </div>
            </div>
        );

    }
}
